import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { LoginService } from '../../login/login.service';
import { ConfigurationsService } from '../../common/_services/configurations.service';
import { Configuration } from '../../common/_types/configuration';

@Component({
    selector: 'app-configurations-list',
    templateUrl: './configurations-list.component.html',
    styleUrls: ['./configurations-list.component.scss'],
    standalone: false
})
export class ConfigurationsListComponent implements OnInit {

    public groupedConfigurations: { group: string, items: any[] }[] = [];
    public loading: boolean = false;
    public allowWrite: boolean = true;

    public booleanOptions = [
        { label: 'Sí', value: 'true' },
        { label: 'No', value: 'false' }
    ];

    constructor(
        private configurationsService: ConfigurationsService,
        private messageService: MessageService,
        private topbarService: TopbarService,
        private loginService: LoginService
    ) { }

    ngOnInit(): void {
        this.topbarService.setTitle('Configuraciones');
        this.allowWrite = this.loginService.hasPermission('COMPANY_CONFIGURATIONS.WRITE');
        this.loadData();
    }

    async loadData(): Promise<void> {
        this.loading = true;
        try {
            const params: IParamsCollection = {
                page: { number: 1, size: 1000 },
                sort: ['group', 'name']
            };
            const response = await firstValueFrom(this.configurationsService.all(params));
            this.buildGroups(response.data);
        } finally {
            this.loading = false;
        }
    }

    private buildGroups(configurations: Configuration[]): void {
        const map = new Map<string, Configuration[]>();
        for (const cfg of configurations) {
            const key = cfg.group || 'Sin grupo';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(cfg);
        }
        this.groupedConfigurations = Array.from(map.entries())
            .map(([group, items]) => ({ group, items }))
            .sort((a, b) => a.group.localeCompare(b.group));

        this.groupedConfigurations.forEach((items: any) => {     
            items.items.forEach((item:any) => {
                item.options = [];
                if(item.controlType=='dropdown'){
                    item.options = JSON.parse(item.configuration).data;
                }
                //los input-number pueden acotar su rango desde el JSON de la fila: { "min": 0, "max": 100 }
                item.min = null;
                item.max = null;
                if(item.controlType=='input-number' && item.configuration){
                    try {
                        const parsed = JSON.parse(item.configuration);
                        item.min = parsed.min ?? null;
                        item.max = parsed.max ?? null;
                    } catch { }
                }
            });            
        });
    }

    getDropdownOptions(configuration: Configuration): any[] {
        if (!configuration.configuration) return [];
        try {
            const parsed = JSON.parse(configuration.configuration);
            return parsed.data || [];
        } catch {
            return [];
        }
    }

    async saveValue(configuration: Configuration): Promise<void> {
        try {
            //p-inputNumber escribe un number en value, que en el recurso es string: la API lo rechaza
            if (configuration.value != null) configuration.value = String(configuration.value);

            const cfg: any = configuration;
            if (cfg.controlType == 'input-number' && configuration.value !== '') {
                const numeric = Number(configuration.value);
                if (cfg.min != null && numeric < cfg.min) {
                    this.messageService.add({ closable: true, severity: 'error', detail: `El valor no puede ser menor que ${cfg.min}` });
                    return;
                }
                if (cfg.max != null && numeric > cfg.max) {
                    this.messageService.add({ closable: true, severity: 'error', detail: `El valor no puede ser mayor que ${cfg.max}` });
                    return;
                }
            }
            await firstValueFrom(this.configurationsService.save(configuration));
            this.messageService.add({ closable: true, severity: 'success', detail: 'Valor guardado correctamente' });
        } catch (error: any) {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error?.title || 'Error al guardar' });
        }
    }

    getValue($event: any){
        console.log($event);
    }
}
